<template>
  <MainCard>
    <MainCardHeader>
      <div class="card-header-row">
        <div class="card-title">
          Prospects: Not In Pipeline
          <InfoBox
            text="No current opportunities but you want to keep them top of mind for future contact."
          />
        </div>
        <SearchField
          @search="handleSearch($event)"
          @cancelSearch="handleSearch($event)"
        />
      </div>
      <CardTableHeader
        :isCheckboxShown="true"
        :isCompanyShown="true"
        :selectedRows="selectedRows"
        :isSelectAllCheckbox="isSelectAllCheckbox"
        :order="order"
        :isNoteShown="true"
        @checkboxSelect="selectAllRows"
        @removeSelection="removeRowsSelection"
        @setOrder="setOrder"
      />
    </MainCardHeader>
    <DividerLine />
    <div
      class="prospects-list"
      v-if="currentPage.length && !errorMessage && !isLoading"
    >
      <draggable
        v-model="currentPage"
        handle=".drag-handle"
        ghost-class="ghost"
        :disabled="order !== '' || isDragDisabled"
        @end="handleDragEnd"
        item-key="id"
      >
        <template #item="{ element }">
          <CardRow
            :key="element.id"
            :id="element.id"
            :note="element.note"
            :order="order"
            :isCheckboxShown="true"
            :isCompanyEditable="true"
            :isNameEditable="true"
            :name="element.name"
            :company="element.company"
            :selectedRows="selectedRows"
            :menuActions="Object.values(menuActions)"
            :actionMenuWidth="'282px'"
            :actionMenuHeight="'245px'"
            :actionMenuTop="'45px'"
            :isSubscriptionActive="isSubscriptionActive"
            @updateName="handleProspectUpdate({ name: $event }, element)"
            @updateCompany="handleProspectUpdate({ company: $event }, element)"
            @updateStatus="handleProspectUpdate({ status: $event }, element)"
            @checkboxSelect="selectOneRow(element.id)"
            @removeSelection="removeOneRowSelection(element.id)"
            @actionMenuItemClick="handleActionMenuClick($event, element.id)"
          />
        </template>
      </draggable>
    </div>
    <div class="error-message" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <Spinner v-if="isLoading" />
    <DividerLine />
    <CardFooter
      :currentPageNumber="currentPageNumber"
      :totalPages="toPages"
      :currentItemsCount="`${fromItems}-${toItems}`"
      :totalItemsCount="totalItemsCount"
      :RowsPerPage="RowsPerPage"
      @setCurrentPerPage="setCurrentPerPage($event)"
      @showPrevPage="getPrevPage"
      @showNextPage="getNextPage"
    />
  </MainCard>
  <ProspectToPipelineModal
    :isModalShown="isModalShown"
    :buttonTitle="buttonTitle"
    :modalError="modalError"
    @setNewDealValue="setNewDealValue"
    @hideModal="hideModal"
    @addToPipeline="addProspectToPipeline"
  />
</template>

<script>
import { mapState, mapActions } from 'vuex'
import draggable from 'vuedraggable'

import MainCard from '../../../common/mainContent/MainCard.vue'
import MainCardHeader from '../../../common/mainContent/MainCardHeader.vue'
import CardTableHeader from '../../../common/mainContent/CardTableHeader.vue'
import DividerLine from '../../../common/DividerLine.vue'
import InfoBox from '../../../common/InfoBox.vue'
import CardRow from '../../../common/mainContent/CardRow.vue'
import CardFooter from '../../../common/mainContent/CardFooter.vue'
import SearchField from '../../../common/header/SearchField.vue'
import ActionMenu from '../../../common/actionMenu/ActionMenu.vue'
import ActionMenuItem from '../../../common/actionMenu/ActionMenuItem.vue'
import ProspectToPipelineModal from './ProspectToPipelineModal.vue'
import Spinner from '../../../common/Spinner.vue'

import { getCurrentDate } from '../../../../utils/helpers'
import {
  ProspectStatus,
  UserSubscriptionStatus,
} from '../../../../utils/constants'

export default {
  components: {
    draggable,
    MainCard,
    MainCardHeader,
    CardTableHeader,
    DividerLine,
    CardRow,
    CardFooter,
    SearchField,
    ActionMenu,
    ActionMenuItem,
    ProspectToPipelineModal,
    Spinner,
    InfoBox,
  },
  emits: [
    'updateInProgressList',
    'updateHotList',
    'updateTargetList',
    'prospectsListWasUpdated',
    'selectOneSubject',
    'removeOneSubjectSelection',
    'selectAllSubjects',
    'removeAllSubjectsSelection',
  ],
  props: ['selectedRows', 'shouldUpdateProspectsList'],
  data() {
    return {
      isLoading: true,
      pageSize: 'All',
      currentPageNumber: 1,
      searchTerm: '',
      order: '',
      isDragDisabled: false,
      errorMessage: '',
      isActionMenuShown: false,
      isModalShown: false,
      isModalLoading: false,
      modalError: '',
      isStatusDropdownOpened: false,
      prospectStatus: ProspectStatus,
      isSelectAllCheckbox: false,
      actionActive: false,
      menuActions: {
        MOVE_TO_HOT: 'Move to Hot',
        MOVE_TO_IN_PROGRESS: 'Move to In Progress',
        MOVE_TO_TARGET: 'Move to Target',
        MOVE_TO_CLIENT: 'Move to Clients',
        ARCHIVE: 'Archive',
      },
      prospectStatusUpdateActions: {
        'Move to Hot': 1,
        'Move to In Progress': 2,
        'Move to Target': 3,
      },
      activeSubscriptionStatuses: [
        UserSubscriptionStatus.Granted,
        UserSubscriptionStatus['Granted Until'],
        UserSubscriptionStatus.Active,
        UserSubscriptionStatus.Canceled,
      ],
      RowsPerPage: ['All', 10, 20, 50],
      newDealValue: '',
      buttonTitle: '',
      currentProspectId: null,
    }
  },
  async created() {
    await this.getFirstPage()
  },
  computed: {
    ...mapState(['currentUser', 'prospects', 'isUndoStack', 'isRedoStack']),
    isSubscriptionActive() {
      return this.activeSubscriptionStatuses.includes(
        this.currentUser.subscription.status
      )
    },
    prospectsList() {
      return this.prospects.prospectsList
    },
    currentPage: {
      get() {
        if (this.pageSize === 'All') return this.prospectsList

        const start =
          this.currentPageNumber === 1
            ? 0
            : this.currentPageNumber * this.pageSize - this.pageSize

        const stop =
          this.currentPageNumber === 1
            ? this.pageSize
            : this.currentPageNumber * this.pageSize

        const page = this.prospectsList.slice(start, stop)

        return page
      },
      set(value) {},
    },
    isUndoStackActive() {
      return this.isUndoStack
    },
    isRedoStackActive() {
      return this.isRedoStack
    },
    totalItemsCount() {
      return this.prospects.prospectsCount
    },
    lastId() {
      return this.currentPage.length ? this.currentPage.pop().id : 0
    },
    pageItemsBase() {
      return this.pageSize * this.currentPageNumber
    },
    fromItems() {
      if (this.pageSize === 'All') return 1

      return this.totalItemsCount === 0
        ? 0
        : this.pageItemsBase + 1 - this.pageSize
    },
    toPages() {
      if (this.pageSize === 'All') return 1
      if (this.totalItemsCount < this.pageSize) return 1

      return Math.ceil(this.totalItemsCount / this.pageSize)
    },
    toItems() {
      if (this.pageSize === 'All') return this.prospectsList.length

      return this.pageItemsBase < this.totalItemsCount
        ? this.pageItemsBase
        : this.totalItemsCount
    },
  },
  watch: {
    async shouldUpdateProspectsList(newValue, oldValue) {
      if (newValue) {
        this.isLoading = true
        this.pageSize = 'All'
        this.currentPageNumber = 1
        this.searchTerm = ''
        this.order = ''

        await this.clearProspects()
        await this.getFirstPage()

        this.$emit('prospectsListWasUpdated')
      }
    },
    modalError(newValue, oldValue) {
      if (newValue) {
        setTimeout(() => {
          this.modalError = ''
        }, 3000)
      }
    },
    async order() {
      this.isLoading = true
      this.currentPageNumber = 1

      await this.clearProspects()
      await this.getFirstPage()
    },
  },
  methods: {
    ...mapActions([
      'getNextProspectsPage',
      'updateProspectsOrder',
      'createProspect',
      'updateProspect',
      'moveProspectsToClients',
      'archiveProspects',
      'clearProspects',
      'addDeals',
      'searchProspect',
      'handelUndoRedoClick',
      'updateDealValue',
    ]),
    getCurrentDate,
    handleGeneralError(error) {
      console.log('==== error while moving prospect to client: ', error)
      this.errorMessage = 'Something went wrong, please try again'
    },
    async getFirstPage() {
      try {
        await this.getNextProspectsPage({
          pageSize: this.pageSize,
          pageNumber: this.currentPageNumber,
          order: this.order,
        })
      } catch (error) {
        this.errorMessage = 'Something went wrong...'
      } finally {
        this.isLoading = false
      }
    },
    async setCurrentPerPage(rowperPage) {
      this.currentPageNumber = 1
      this.pageSize = rowperPage
      await this.clearProspects()
      await this.getFirstPage()
    },
    async handleSearch(searchValue) {
      this.isLoading = true
      this.searchTerm = searchValue
      this.currentPageNumber = 1

      const payload = {
        order: this.order,
        searchTerm: searchValue,
        pageSize: this.pageSize,
        pageNumber: this.currentPageNumber,
      }

      try {
        await this.searchProspect(payload)
      } catch (error) {
        this.errorMessage = 'Something went wrong...'
      } finally {
        this.isLoading = false
      }
    },
    async Undo() {
      this.currentPageNumber = 1
      await this.handelUndoRedoClick('undoStack')
      await this.clearProspects()
      await this.getFirstPage()
    },
    async Redo() {
      this.currentPageNumber = 1
      await this.handelUndoRedoClick('redoStack')
      await this.clearProspects()
      await this.getFirstPage()
    },
    setOrder(evt) {
      if (this.isLoading) return
      this.order = evt
    },
    async handleDragEnd(evt) {
      this.isDragDisabled = true

      const { oldIndex, newIndex } = evt

      if (oldIndex === newIndex) return

      const elementId = this.currentPage[oldIndex].id

      try {
        await this.updateProspectsOrder({
          elementId,
          oldLocalIndex: oldIndex,
          newLocalIndex: newIndex,
        })
      } catch (error) {
        this.handleGeneralError(error)
      } finally {
        this.isDragDisabled = false
      }
    },
    async getNextPage() {
      if (
        (this.currentPageNumber !== 0 &&
          this.toItems === this.totalItemsCount) ||
        this.isLoading
      )
        return

      this.currentPageNumber += 1
      this.removeRowsSelection()

      if (this.currentPage.length) return

      this.isLoading = true

      try {
        await this.getNextProspectsPage({
          pageSize: this.pageSize,
          pageNumber: this.currentPageNumber,
          searchTerm: this.searchTerm,
          order: this.order,
        })
      } catch (error) {
        this.errorMessage = 'Something went wrong...'
      } finally {
        this.isLoading = false
      }
    },
    getPrevPage() {
      if (this.currentPageNumber === 1) return

      this.removeRowsSelection()
      this.currentPageNumber -= 1
    },
    showModal() {
      if (!this.isSubscriptionActive) return
      this.isModalShown = true
    },
    hideModal() {
      this.isModalShown = false
      this.currentProspectId = null
      this.buttonTitle = ''
      this.newDealValue = ''
      this.modalError = ''
    },
    setNewDealValue(newDealValue) {
      this.newDealValue = newDealValue
    },
    async handleNewProspectCreation() {
      if (!this.newProspectName || !this.newProspectCompany) {
        this.modalError = 'Prospect name and company are required'
        return
      }

      const payload = {
        new_prospect: {
          name: this.newProspectName,
          company: this.newProspectCompany,
          status: this.prospectStatus[this.newProspectStatus],
        },
      }

      try {
        this.isModalLoading = true

        await this.createProspect(payload)

        this.isModalShown = false
        this.order = ''
        this.newProspectName = ''
        this.newProspectCompany = ''
      } catch (error) {
        console.log('==== error while creating prospect: ', error)
        this.modalError = 'Something went wrong, please try again'
      } finally {
        this.isModalLoading = false
      }
    },
    selectAllRows() {
      const ids = this.currentPage.map((item) => item.id)
      this.$emit('selectAllSubjects', ids)
    },
    removeRowsSelection() {
      this.$emit('removeAllSubjectsSelection')
    },
    selectOneRow(rowId) {
      this.$emit('selectOneSubject', rowId)

      if (this.selectedRows.length === this.currentPage.length) {
        this.isSelectAllCheckbox = true
      }
    },
    removeOneRowSelection(rowId) {
      this.$emit('removeOneSubjectSelection', rowId)
      this.isSelectAllCheckbox = false
    },
    async handleProspectUpdate(partialProspectUpdate, prospect) {
      if (!this.isSubscriptionActive) return

      this.isLoading = true

      const updatedProspect = { ...prospect, ...partialProspectUpdate }
      await this.updateProspect(updatedProspect)

      if ('status' in partialProspectUpdate) {
        await this.updateDealValue({
          subjectId: prospect.id,
          dealValue: this.newDealValue,
        })

        this.newDealValue = ''
        this.currentPageNumber = 1

        await this.clearProspects()
        await this.getFirstPage()

        this.removeRowsSelection()

        if (
          partialProspectUpdate.status === this.prospectStatus['In Progress']
        ) {
          this.$emit('updateInProgressList')
        } else if (partialProspectUpdate.status === this.prospectStatus.Hot) {
          this.$emit('updateHotList')
        } else if (
          partialProspectUpdate.status === this.prospectStatus.Target
        ) {
          this.$emit('updateTargetList')
        }
      } else {
        this.isLoading = false
      }
    },
    async addProspectToPipeline() {
      const isAllNum = /^\d+$/.test(this.newDealValue)

      if (!this.newDealValue || !isAllNum) {
        this.modalError = 'Deal value should contain only digits'
        return
      }

      const prospect = this.currentPage.find(
        (i) => i.id === this.currentProspectId
      )
      const partialUpdate = {
        status: this.prospectStatusUpdateActions[this.buttonTitle],
      }

      this.buttonTitle = ''
      this.isModalShown = false
      this.currentProspectId = null

      try {
        await this.handleProspectUpdate(partialUpdate, prospect)
      } catch (error) {
        this.handleGeneralError(error)
      } finally {
        this.isLoading = false
      }
    },
    async handleActionMenuClick(action, prospectId = null) {
      this.isActionMenuShown = false

      // If we want to move prospect to another status (to pipeline)
      if (Object.keys(this.prospectStatusUpdateActions).includes(action)) {
        this.buttonTitle = action
        this.currentProspectId = prospectId
        this.isModalShown = true

        return
      }

      this.isLoading = true

      const prospectIds = prospectId ? [prospectId] : this.selectedRows

      if (!prospectIds.length) {
        this.isLoading = false
        return
      }

      try {
        if (action === this.menuActions.MOVE_TO_CLIENT) {
          await this.moveProspectsToClients(prospectIds)
        }

        if (action === this.menuActions.ARCHIVE) {
          await this.archiveProspects(prospectIds)
        }

        this.currentPageNumber = 1

        await this.clearProspects()
        await this.getNextProspectsPage({
          pageSize: this.pageSize,
          pageNumber: this.currentPageNumber,
          order: this.order,
        })

        this.removeRowsSelection()
      } catch (error) {
        this.handleGeneralError(error)
      } finally {
        this.isLoading = false
      }
    },
  },
  async beforeUnmount() {
    await this.clearProspects()
  },
}
</script>

<style lang="scss" scoped>
@import '../../../../assets/styleVars.scss';

.main-card {
  height: 655px;
  min-height: 655px;

  .card-header-row {
    width: calc(100% - 80px);
    height: calc(100% - 47px);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .card-title {
      font-size: $fontSizeTitle;
    }
  }
}

@media (max-width: 420px) {
  .card-footer {
    width: 980px;
  }
  .card-footer:deep(.rows-per-page) {
    margin-left: 340px !important;
  }

  .prospects-list {
    overflow-y: visible;

    .card-row {
      .editable-field:deep(.value-container) {
        width: 150px;
        min-width: 150px !important;
      }
    }
  }

  .divider-line {
    width: 980px;
  }

  .card-table-header {
    width: calc(100% - 20px) !important;
  }

  .action-button {
    width: 78px !important;
    font-size: $fontSizeActionButtonMinified;
    padding-left: 5px;
  }
}
</style>

<style lang="scss">
@import '../../../../assets/styleVars.scss';

.prospects-list {
  width: 100%;
  height: calc(100% - 204px);
  overflow-y: scroll;

  .ghost {
    opacity: 0.9;
    background: $appActionColor;
  }
}

.error-message {
  width: 100%;
  height: calc(100% - 104px);
  display: flex;
  justify-content: center;
  align-items: center;
  color: $redWarningColor;
}
</style>
